import {
  convertFromQueryToNewUiFormat,
  DATE_RANGE_TYPE,
  FilterKindEnum,
  QueryFormatFilter,
} from '@hotjar/global-filters';
import { useTargetingSettings } from '@hotjar/webapp/hooks/useTargetingSettings';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { WatchedHotjarFilter } from '../../../store/list/types';
import { DefaultEmptyState } from './DefaultEmptyState';
import { NoDataForDateRangeEmptyState } from './NoDataForDateRangeEmptyState';
import { ErrorCapturingDisabledEmptyState } from './ErrorCapturingDisabledEmptyState';

import type { Site } from '@hotjar/site';

export interface RecordingsListEmptyStateProps {
  onButtonClick: () => void;
  onFiltersChangePromptClick: () => void;
  watchedHotjarFilter: WatchedHotjarFilter;
  jsonFilters: QueryFormatFilter;
  site: Site;
}

export const RecordingsListEmptyState = ({
  onButtonClick,
  onFiltersChangePromptClick,
  watchedHotjarFilter,
  jsonFilters,
  site,
}: RecordingsListEmptyStateProps): JSX.Element => {
  const readableFilters = convertFromQueryToNewUiFormat(jsonFilters);
  const { sessionCaptureConsoleConsent } = useTargetingSettings(site);
  const { hasPermission } = useAuthorization();

  const hasSessionSettingsUpdatePermission = hasPermission(
    'site',
    `${site.id}`,
    'session-settings-update',
  );

  const isDateRangeIncompatibleWithV3 =
    readableFilters.dateRange &&
    readableFilters.dateRange.type === DATE_RANGE_TYPE.ABSOLUTE &&
    readableFilters.dateRange.to <= new Date(1656975600000); // July 5th 2022
  const hasClickedFilter = Boolean(
    readableFilters.filterList.find((filter) => filter.kind === FilterKindEnum.Clicked),
  );
  const hasErrorFilter = Boolean(
    readableFilters.filterList.find((filter) => filter.kind === FilterKindEnum.Error),
  );

  if (hasErrorFilter && !sessionCaptureConsoleConsent) {
    return (
      <ErrorCapturingDisabledEmptyState
        hasSessionSettingsUpdatePermission={hasSessionSettingsUpdatePermission}
        siteId={site?.id}
      />
    );
  }

  if (isDateRangeIncompatibleWithV3 && hasClickedFilter) {
    return <NoDataForDateRangeEmptyState />;
  }

  return (
    <DefaultEmptyState
      onButtonClick={onButtonClick}
      onFiltersChangePromptClick={onFiltersChangePromptClick}
      watchedHotjarFilter={watchedHotjarFilter}
    />
  );
};
